/**
 * AlignifyNow - Main App Router
 * 
 * ROUTING RULES:
 * - "/" = PUBLIC Landing Page (Home.jsx) - NO AUTH REQUIRED
 * - "/pricing" = PUBLIC Pricing Page - NO AUTH REQUIRED
 * - "/terms" = PUBLIC Terms Page - NO AUTH REQUIRED
 * - "/tools/*" = PUBLIC Free Tools - NO AUTH REQUIRED
 * - "/tracker" = GATED Job Tracker - Shows FeatureGate (Google Sign-In) if not authenticated
 * - "/tools/mock-interview" = PROTECTED Mock Interview - Requires auth
 * - "/login" = REDIRECTS to /tracker (no Magic Link)
 * - "/register" = REDIRECTS to /tracker (no Magic Link)
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Page Components
import Home from './components/Home';
import Pricing from './components/Pricing';
import Terms from './components/Terms';
import PrivacyPolicy from './components/PrivacyPolicy';
import CoverLetterStarter from './components/CoverLetterStarter';
import MatchLite from './components/MatchLite';
import MatchLiteV2 from './components/MatchLiteV2';
import JobTracker from './components/JobTracker';
import FeatureGate from './components/FeatureGate';
import AuthCallback from './components/AuthCallback';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import PaymentSuccess from './components/PaymentSuccess';
import MockInterview from './components/MockInterview';
import MatchScore from './components/MatchScore';
import Blog from './components/Blog';
import BlogPost from './components/BlogPost';
import Affiliates from './components/Affiliates';
import ChecklistDownload from './components/ChecklistDownload';
import FutureProofAudit from './components/FutureProofAudit';

import './App.css';

/**
 * FeatureGatedRoute - Shows FeatureGate if not authenticated, otherwise shows children
 */
const FeatureGatedRoute = ({ children, feature, headline, description, benefits }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <FeatureGate 
        feature={feature}
        headline={headline}
        description={description}
        benefits={benefits}
      />
    );
  }

  return children;
};

/**
 * ProtectedRoute - Redirects to /tracker if not authenticated
 */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/tracker" replace />;
};

/**
 * AdminRoute - Requires admin role
 */
const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/tracker" replace />;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/tracker" replace />;
  }

  return children;
};

/**
 * Main App Content with Routes
 */
const AppContent = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* ============================================ */}
        {/* PUBLIC ROUTES - NO AUTH REQUIRED            */}
        {/* ============================================ */}
        
        {/* Landing Page - THE ROOT ROUTE IS PUBLIC */}
        <Route path="/" element={<Home />} />
        
        {/* Pricing Page - PUBLIC */}
        <Route path="/pricing" element={<Pricing />} />
        
        {/* Terms of Service - PUBLIC */}
        <Route path="/terms" element={<Terms />} />
        
        {/* Privacy Policy - PUBLIC */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        
        {/* Affiliates - PUBLIC */}
        <Route path="/affiliates" element={<Affiliates />} />
        
        {/* Checklist Download - PUBLIC (Lead Magnet) */}
        <Route path="/checklist" element={<ChecklistDownload />} />
        
        {/* Blog - PUBLIC (No auth required) */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        
        {/* Match Score - PUBLIC (dedicated page, no redirect for logged-in users) */}
        <Route path="/match" element={<MatchScore />} />
        
        {/* Free Tools - PUBLIC */}
        <Route path="/tools/match-lite" element={<MatchLite />} />
        <Route path="/tools/match-lite-v2" element={<MatchLiteV2 />} />
        <Route path="/tools/cover-letter" element={<CoverLetterStarter />} />
        
        {/* Future-Proof Audit - Accessible to all, shows sign-in prompt if not auth'd */}
        <Route path="/tools/future-proof" element={<FutureProofAudit />} />
        
        {/* Mock Interview - PROTECTED (requires auth) */}
        <Route
          path="/tools/mock-interview"
          element={
            <ProtectedRoute>
              <MockInterview />
            </ProtectedRoute>
          }
        />
        
        {/* Google OAuth Callback */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* ============================================ */}
        {/* GATED ROUTES - SHOW GOOGLE SIGN-IN GATE     */}
        {/* ============================================ */}
        
        {/* Job Tracker - Shows FeatureGate with Google Sign-In if not authenticated */}
        <Route
          path="/tracker"
          element={
            <FeatureGatedRoute
              feature="Job Tracker"
              headline="Unlock the Job Tracker"
              description="Track up to 5 active job applications, set reminders, and never lose track of where you applied."
              benefits={[
                'Track status privately (Data syncs to your Google Drive)',
                'Track status: Applied, Interview, Offer, Rejected',
                'Connect to Google Drive for data backup',
                'Set reminders for follow-ups'
              ]}
            >
              <JobTracker />
            </FeatureGatedRoute>
          }
        />

        {/* ============================================ */}
        {/* PROTECTED ROUTES - REQUIRE AUTH             */}
        {/* ============================================ */}
        
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        
        <Route
          path="/payment-success"
          element={
            <ProtectedRoute>
              <PaymentSuccess />
            </ProtectedRoute>
          }
        />

        {/* ============================================ */}
        {/* LEGACY REDIRECTS - NO MAGIC LINK            */}
        {/* ============================================ */}
        
        {/* /login redirects to /tracker (Google Sign-In) */}
        <Route path="/login" element={<Navigate to="/tracker" replace />} />
        
        {/* /register redirects to /tracker (Google Sign-In) */}
        <Route path="/register" element={<Navigate to="/tracker" replace />} />
        
        {/* Old magic link verify route - redirect to home */}
        <Route path="/auth/verify" element={<Navigate to="/" replace />} />

        {/* ============================================ */}
        {/* 404 - NOT FOUND                             */}
        {/* ============================================ */}
        
        <Route
          path="*"
          element={
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                <p className="text-gray-600 mb-8">Page not found</p>
                <a
                  href="/"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Go to Home
                </a>
              </div>
            </div>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

import ErrorBoundary from './components/ErrorBoundary';

/**
 * Root App Component
 */
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
